import * as mobx from 'mobx';
import { IntlShape } from 'react-intl';
import * as IoC from 'typescript-ioc';
import { IAccountDetailsDTO, IAccountsDAO } from '../../../api/accounts.dao';
import { MAX_THEME_CODE_FILE_SIZE, MINIMAL_SEARCH_LENGTH, ONE_MIB, SHORT_PLAIN_TEXT_LENGTH } from '../../../constants/common.constants';
import { ICreateThemeDTO, IThemeDTO, IUpdateThemeDTO } from '../../../api/themes.dao';
import { COMMON_ITEMS_PER_PAGE } from '../../../constants/pagination.contants';
import { SUPPORTED_ZIP_TYPES } from '../../../constants/file.constants';

class ThemeFormDataVM {
    public defaultValues = {
        name: '',
        themeArchive: null,
        isFormChecked: false,
        status: 'PENDING',
        isAllAccountsChecked: false,
    };

    @mobx.observable
    public themeId: number;

    @mobx.observable
    public name: string = this.defaultValues.name;

    @mobx.observable
    public themeArchive: File = this.defaultValues.themeArchive;

    @mobx.observable
    public isFormChecked = this.defaultValues.isFormChecked;

    @mobx.observable
    public serverErrorMessage: string;

    @mobx.observable
    public validationError: string[];

    @mobx.observable
    public assignedAccounts: IAccountDetailsDTO[];

    @mobx.observable
    public selectableAccounts: IAccountDetailsDTO[];

    @mobx.observable
    public isAllAccountsChecked = this.defaultValues.isAllAccountsChecked;

    constructor(
        @IoC.Inject private readonly accountsDAO: IAccountsDAO = IoC.Container.get(IAccountsDAO),
    ) {
        this.assignedAccounts = [];
    }


    public async initialize(theme: IThemeDTO) {
        if (theme) {
            this.name = theme.name;
            this.themeId = theme.id;
            this.isAllAccountsChecked = !!theme.allAccounts;
            if (theme.accounts && !theme.allAccounts) {
                this.setAssignedAccounts(theme.accounts);
            }
        }
    }

    @mobx.action
    public setName = (name: string) => {
        this.serverErrorMessage = '';
        this.name = name;
    };

    @mobx.action
    public handleAllAccountsCheckbox = (isAllAccountsChecked: boolean) => {
        this.isAllAccountsChecked = !isAllAccountsChecked;
    };

    public fetchAccountsList = async (query?: string): Promise<void> => {
        const response = await this.accountsDAO.getAccounts({
            page: 0,
            size: COMMON_ITEMS_PER_PAGE,
            query: query?.trim() || ''
        });
        mobx.runInAction(() => {
            this.selectableAccounts = response.content;
        });
    };

    @mobx.action
    public searchAccounts = (query: string): void => {
        if (!query || query.trim().length >= MINIMAL_SEARCH_LENGTH) {
            this.fetchAccountsList(query);
        }
    };

    public removeAccount = (assignedUsers: IAccountDetailsDTO[], id: number) => {
        this.assignedAccounts = assignedUsers.filter(({ accountId }) => accountId !== id);
    };

    @mobx.action
    public setAssignedAccounts = (accounts: IAccountDetailsDTO[]) => {
        this.assignedAccounts = accounts;
    };

    @mobx.action
    public setThemeArchive = (themeArchive: File) => {
        this.themeArchive = themeArchive;
    };

    public getThemeNameError(intl: IntlShape): string | null {
        if (!this.name) {
            return intl.formatMessage({
                id: `oe.theme.themeNameIsRequired`,
                defaultMessage: 'Theme Name is required',
                description: 'Error message in theme popover'
            });
        } else if (this.name.length > SHORT_PLAIN_TEXT_LENGTH) {
            return intl.formatMessage({
                id: `oe.themeName.tooLong`,
                defaultMessage: 'Too long - max length is 255 characters',
                description: 'Error message text'
            });
        }

        return null;
    }

    public getThemeArchiveError(intl: IntlShape): string | null {
        if (!this.themeArchive) {
            return this.themeId ? null : intl.formatMessage({
                id: `oe.theme.themeArchiveIsRequired`,
                defaultMessage: 'Theme Code is required',
                description: 'Error message in theme popover'
            });
        } else if ((this.themeArchive.size / ONE_MIB) > MAX_THEME_CODE_FILE_SIZE) {
            return intl.formatMessage({
                id: `oe.themeArchive.tooLarge`,
                defaultMessage: 'File size is greater than 1Gb. Please, upload the file below 1Gb.',
                description: 'Error message text'
            });
        } else if (!SUPPORTED_ZIP_TYPES.includes(this.themeArchive.type)
            || (this.themeArchive.type === 'application/octet-stream' && !this.themeArchive.name.endsWith('.zip'))) {
            return intl.formatMessage({
                id: `oe.themeArchive.isNotZip`,
                defaultMessage: 'Unsupported archive type. Please, upload file with .zip extension.',
                description: 'Error message text'
            });
        }

        return null;
    }

    public isFormValid(intl: IntlShape): boolean {
        return !this.getThemeNameError(intl)
            && !this.getThemeArchiveError(intl);
    }

    @mobx.action
    public allowToShowValidationErrors(): void {
        this.isFormChecked = true;
    }

    public showValidationErrors(): boolean {
        return this.isFormChecked;
    }

    @mobx.action
    public setServerErrorMessage(message: string): void {
        this.serverErrorMessage = message;
    }

    public getServerErrorMessage(): string {
        return this.serverErrorMessage;
    }

    public getAccountIds = () => {
        let accountIds = null;
        if (!this.isAllAccountsChecked) {
            accountIds = this.assignedAccounts.map(account => account.accountId);
        }
        return accountIds;
    };

    public toApiCreateThemeDTO(themeArchive: File): ICreateThemeDTO {
        return {
            name: this.name.trim() || null,
            themeArchive: themeArchive,
            allAccounts: this.isAllAccountsChecked,
            accountIds: this.getAccountIds(),
        };
    }

    public toApiUpdateThemeDTO(themeArchive?: File): IUpdateThemeDTO {
        return {
            id: this.themeId,
            name: this.name.trim() || null,
            themeArchive: themeArchive || null,
            allAccounts: this.isAllAccountsChecked,
            accountIds: this.getAccountIds(),
        };
    }
}

export { ThemeFormDataVM };